"use client";

import { Settings2, Trash2 } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { Button } from "@/components/tailwind/ui/button";
import { Checkbox } from "@/components/tailwind/ui/checkbox";
import { Input } from "@/components/tailwind/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/tailwind/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/tailwind/ui/table";

export interface Column<T> {
  id: string;
  header: string;
  accessor?: (row: T) => any;
  render?: (row: T) => ReactNode;
  sortable?: boolean;
  sortKey?: string;
  visible?: boolean;
  canToggle?: boolean; // Whether this column can be toggled on/off
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  getRowId: (row: T) => string;
  onDelete?: (ids: string[]) => void;
  onValidate?: (ids: string[]) => void;
  validateButtonText?: string;
  onEdit?: (ids: string[]) => void;
  editButtonText?: string;
  onRowClick?: (row: T) => void;
  searchPlaceholder?: string;
  searchKeys?: (keyof T)[];
  additionalFilters?: ReactNode;
  loading?: boolean;
}

export default function DataTable<T>({
  data,
  columns,
  getRowId,
  onDelete,
  onValidate,
  validateButtonText = "Validate Selected",
  onEdit,
  editButtonText = "Edit Selected",
  onRowClick,
  searchPlaceholder = "Search...",
  searchKeys = [],
  additionalFilters,
  loading = false,
}: DataTableProps<T>) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    columns.reduce(
      (acc, col) => {
        acc[col.id] = col.visible !== false;
        return acc;
      },
      {} as Record<string, boolean>,
    ),
  );

  // Filter data based on search
  const filteredData = data.filter((row) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return searchKeys.some((key) => {
      const value = row[key];
      return String(value).toLowerCase().includes(searchLower);
    });
  });

  // Sort data
  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortBy) return 0;

    const column = columns.find((col) => col.id === sortBy);
    if (!column) return 0;

    const aValue = column.accessor ? column.accessor(a) : a[sortBy as keyof T];
    const bValue = column.accessor ? column.accessor(b) : b[sortBy as keyof T];

    if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
    if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(new Set(sortedData.map(getRowId)));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedRows(newSelected);
  };

  const handleDeleteSelected = () => {
    if (selectedRows.size === 0 || !onDelete) return;

    if (confirm(`Are you sure you want to delete ${selectedRows.size} selected item(s)?`)) {
      onDelete(Array.from(selectedRows));
      setSelectedRows(new Set());
    }
  };

  const handleValidateSelected = () => {
    if (selectedRows.size === 0 || !onValidate) return;
    onValidate(Array.from(selectedRows));
  };

  const handleEditSelected = () => {
    if (selectedRows.size === 0 || !onEdit) return;
    onEdit(Array.from(selectedRows));
  };

  const handleSort = (columnId: string, sortKey?: string) => {
    const key = sortKey || columnId;
    if (sortBy === key) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(key);
      setSortOrder("asc");
    }
  };

  const toggleColumn = (columnId: string) => {
    setVisibleColumns((prev) => ({ ...prev, [columnId]: !prev[columnId] }));
  };

  const visibleColumnsList = columns.filter((col) => visibleColumns[col.id]);
  const toggleableColumns = columns.filter((col) => col.canToggle !== false);

  return (
    <div className="space-y-4">
      <div className="flex gap-4 flex-wrap">
        <Input
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-sm"
        />
        {additionalFilters}
        {onDelete && (
          <Button variant="destructive" size="sm" onClick={handleDeleteSelected} disabled={selectedRows.size === 0}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Selected ({selectedRows.size})
          </Button>
        )}
        {onValidate && (
          <Button variant="outline" size="sm" onClick={handleValidateSelected} disabled={selectedRows.size === 0}>
            {validateButtonText} ({selectedRows.size})
          </Button>
        )}
        {onEdit && (
          <Button variant="outline" size="sm" onClick={handleEditSelected} disabled={selectedRows.size === 0}>
            {editButtonText} ({selectedRows.size})
          </Button>
        )}
        {toggleableColumns.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 className="h-4 w-4 mr-2" />
                Columns
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56">
              <div className="space-y-2">
                <h4 className="font-medium text-sm mb-2">Toggle Columns</h4>
                {toggleableColumns.map((col) => (
                  <div key={col.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`col-${col.id}`}
                      checked={visibleColumns[col.id]}
                      onCheckedChange={() => toggleColumn(col.id)}
                    />
                    <label htmlFor={`col-${col.id}`} className="text-sm cursor-pointer">
                      {col.header}
                    </label>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading...</div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                {onDelete && (
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedRows.size === sortedData.length && sortedData.length > 0}
                      onCheckedChange={handleSelectAll}
                      aria-label="Select all"
                    />
                  </TableHead>
                )}
                {visibleColumnsList.map((col) => (
                  <TableHead
                    key={col.id}
                    className={col.sortable ? "cursor-pointer" : ""}
                    onClick={() => col.sortable && handleSort(col.id, col.sortKey)}
                  >
                    {col.header}{" "}
                    {col.sortable && sortBy === (col.sortKey || col.id) && (sortOrder === "asc" ? "↑" : "↓")}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumnsList.length + (onDelete ? 1 : 0)}
                    className="text-center py-8 text-gray-500"
                  >
                    No data found
                  </TableCell>
                </TableRow>
              ) : (
                sortedData.map((row) => {
                  const rowId = getRowId(row);
                  return (
                    <TableRow
                      key={rowId}
                      className={onRowClick ? "cursor-pointer hover:bg-muted/50" : ""}
                      onDoubleClick={() => onRowClick?.(row)}
                    >
                      {onDelete && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedRows.has(rowId)}
                            onCheckedChange={(checked) => handleSelectRow(rowId, checked as boolean)}
                            aria-label={`Select row ${rowId}`}
                          />
                        </TableCell>
                      )}
                      {visibleColumnsList.map((col) => (
                        <TableCell key={col.id}>
                          {col.render ? col.render(row) : col.accessor ? col.accessor(row) : row[col.id as keyof T]}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
